import type { Component } from 'solid-js'
import { createEffect, createMemo, createSignal, createUniqueId, mergeProps, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import FieldMessage from '~/components/FieldMessage'
import Icon from '~/components/Icon'
import { useFieldError } from '~/hooks/useFieldError'
import type { MainSizesType } from '~/types/components'
import type { MaskInputPropsType } from '~/types/MaskInput'
import { controlSize } from '~/utils/controlSize'

const isIconifyIcon = (icon: unknown): icon is { body: string } => typeof icon === 'object' && icon !== null && 'body' in icon

const formatPercentage = (value: string): string => {
  if (!value) return ''
  const num = parseNumberWithPrecision(value, 2)
  if (Number.isNaN(num)) return ''
  const str = String(value)
  const hasDot = str.includes('.')
  if (hasDot) {
    return `${str}%`
  } else {
    return `${num}%`
  }
}

const formatCreditCard = (value: string): string => {
  if (!value) return ''
  return value.replace(/(\d{4})(?=\d)/g, '$1 ').trim()
}

const MAX_SAFE_DIGITS = Number.MAX_SAFE_INTEGER
const DEFAULT_MAX_CURRENCY = 999999999999.99
const MAX_STRING_LENGTH = 50

const _isValidNumericString = (str: string): boolean => {
  if (!str || str.length === 0 || str.length > MAX_STRING_LENGTH) return false

  const numericRegex = /^-?\d+(\.\d+)?$/
  return numericRegex.test(str)
}

const compareNumericStrings = (str1: string, str2: string): number => {
  const sign1 = str1.startsWith('-') ? -1 : 1
  const sign2 = str2.startsWith('-') ? -1 : 1

  if (sign1 !== sign2) return sign1 > sign2 ? 1 : -1

  const abs1 = str1.replace('-', '')
  const abs2 = str2.replace('-', '')

  if (abs1.length !== abs2.length) {
    return abs1.length > abs2.length ? sign1 : -sign1
  }

  return abs1.localeCompare(abs2) * sign1
}

const roundToPrecision = (num: number, decimals: number): number => {
  if (!Number.isFinite(num) || Number.isNaN(num)) return 0
  const factor = 10 ** decimals
  return Math.round(num * factor) / factor
}

const parseNumberWithPrecision = (str: string, maxDecimals: number = 10): number => {
  if (!str || str.trim() === '') return 0

  const cleanStr = str.replace(/[^0-9.-]/g, '')

  const num = roundToPrecision(parseFloat(cleanStr) || 0, maxDecimals)

  if (Number.isNaN(num) || !Number.isFinite(num)) return 0

  return num
}

/** Símbolo que se antepone a la moneda cuando `currencyDisplay = 'symbol'`. */
const CURRENCY_SYMBOLS: Record<string, string> = {
  COP: '$',
  USD: 'US$',
  EUR: '€',
  MXN: '$',
  GBP: '£',
  CLP: '$',
  ARS: '$',
  BRL: 'R$'
}

const formatCurrency = (num: number, currency: string, currencyDisplay: 'symbol' | 'code' | 'name', minFrac: number, maxFrac: number, group = ',', decimal = '.'): string => {
  if (!Number.isFinite(num) || Number.isNaN(num)) {
    return currencyDisplay === 'symbol' ? '$ 0.00' : 'USD 0.00'
  }

  let numStr: string
  if (Math.abs(num) > MAX_SAFE_DIGITS) {
    numStr = String(num)
  } else {
    const roundedNum = roundToPrecision(num, maxFrac)
    numStr = String(roundedNum)
  }

  const symbol = currencyDisplay === 'symbol' ? (CURRENCY_SYMBOLS[currency] ?? currency) : currency

  const isNegative = numStr.startsWith('-')
  const absNumStr = isNegative ? numStr.substring(1) : numStr

  const [intPart, decPart] = absNumStr.split('.')

  const intWithCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, group)

  const decFormatted = decPart ? `${decimal}${decPart.padEnd(minFrac, '0').substring(0, maxFrac)}` : minFrac > 0 ? `${decimal}${'0'.repeat(minFrac)}` : ''

  const result = `${symbol} ${intWithCommas}${decFormatted}`
  return isNegative ? `-${result}` : result
}

const MaskInput: Component<MaskInputPropsType> = props => {
  const id = createUniqueId()
  const merged = mergeProps(
    {
      variant: 'default',
      size: 'md' as MainSizesType
    },
    props
  )
  const [local, _inputProps] = splitProps(merged, [
    'maskType',
    'customMask',
    'value',
    'onValueChange',
    'class',
    'variant',
    'size',
    'locale',
    'currency',
    'fractionDigits',
    'maxFractionDigits',
    'currencyDisplay',
    'min',
    'max',
    'startIcon',
    'endIcon'
  ])

  // Separate 'name' so it is NOT spread onto the visible input
  // (the hidden input handles form submission with the raw value)
  const [nameProps, inputProps] = splitProps(_inputProps, ['name'])

  // Sin prop `error`, el campo lee su error del contexto del `<Form>`. La prop
  // explícita gana sobre el contexto.
  const resolvedError = useFieldError(
    () => nameProps.name,
    () => inputProps.error
  )

  const [_rawValue, setRawValue] = createSignal('')
  const [displayValue, setDisplayValue] = createSignal('')

  const [isLabelFloated, setIsLabelFloated] = createSignal(false)
  const [hasFocus, setHasFocus] = createSignal(false)

  createEffect(() => {
    if (inputProps.label) {
      const hasValue =
        displayValue() &&
        displayValue() !== '' &&
        displayValue() !== '$0.00' &&
        displayValue() !== '0%' &&
        displayValue() !== '0000 0000 0000 0000' &&
        displayValue() !== '_' &&
        displayValue() !== inputProps.placeholder
      setIsLabelFloated(hasValue || hasFocus())
    }
  })
  let inputRef: HTMLInputElement | undefined

  /**
   * Separadores de miles y decimales del locale indicado. Estaban fijos en
   * formato inglés, así que `locale="es-CO"` mostraba 12,500,000 en vez de
   * 12.500.000. Se leen de Intl para no mantener una tabla por país.
   */
  const numberSymbols = createMemo(() => {
    if (local.maskType !== 'currency') return { group: ',', decimal: '.' }
    try {
      const parts = new Intl.NumberFormat(local.locale || 'en-US').formatToParts(12345.6)
      return {
        group: parts.find(p => p.type === 'group')?.value || ',',
        decimal: parts.find(p => p.type === 'decimal')?.value || '.'
      }
    } catch {
      return { group: ',', decimal: '.' }
    }
  })

  const fractionDigitsUsed = createMemo(() => {
    if (local.maskType !== 'currency') return 0
    return typeof local.fractionDigits === 'number' ? local.fractionDigits : 0
  })

  const maxFractionDigitsAllowed = createMemo(() => {
    if (local.maskType !== 'currency') return 0
    if (typeof local.maxFractionDigits === 'number') return local.maxFractionDigits
    return 10
  })

  createEffect(() => {
    if (local.value !== undefined && local.value !== null) {
      const newValue = String(local.value)
      if (newValue !== _rawValue()) {
        setRawValue(newValue)
        updateDisplayValue(newValue)
      }
    }
  })

  type MaskToken = { kind: '9' | 'a' | '*' } | { kind: 'literal'; char: string }
  const buildMaskTokens = (mask: string): MaskToken[] => {
    const tokens: MaskToken[] = []
    for (const ch of mask) {
      if (ch === '9' || ch === 'a' || ch === '*') tokens.push({ kind: ch })
      else tokens.push({ kind: 'literal', char: ch })
    }
    return tokens
  }
  const isNumericMask = (mask: string): boolean => {
    const tokens = buildMaskTokens(mask)
    return tokens.every(token => token.kind === 'literal' || token.kind === '9')
  }
  const isAllowedForToken = (ch: string, token: MaskToken) => {
    if (token.kind === '9') return /\d/.test(ch)
    if (token.kind === 'a') return /[A-Za-z]/.test(ch)
    if (token.kind === '*') return /[A-Za-z0-9]/.test(ch)
    return false
  }
  const maxInputLenForMask = (tokens: MaskToken[]) => tokens.filter(t => t.kind !== 'literal').length
  const applyMask = (raw: string, tokens: MaskToken[], slotChar: string = '_') => {
    if (!raw) return tokens.map(t => (t.kind === 'literal' ? t.char : slotChar)).join('')
    let ri = 0
    let out = ''
    for (const t of tokens) {
      if (t.kind === 'literal') {
        out += t.char
      } else {
        if (ri >= raw.length) {
          out += slotChar
        } else {
          const ch = raw[ri]
          if (isAllowedForToken(ch, t)) {
            out += ch
            ri++
          } else {
            out += slotChar
          }
        }
      }
    }
    return out
  }

  const updateDisplayValue = (value: string) => {
    if (!value) {
      setDisplayValue('')
      return
    }

    switch (local.maskType) {
      case 'currency': {
        const baseCurrency = local.currency || 'USD'
        const maxFrac = fractionDigitsUsed() ?? 0
        const str = String(value)
        const hasDot = str.includes('.')
        const decimals = hasDot ? (str.split('.')[1] || '').length : 0
        const effectiveMaxFrac = Math.max(maxFrac, decimals)
        const opts = {
          minimumFractionDigits: hasDot && decimals === 0 ? effectiveMaxFrac : decimals,
          maximumFractionDigits: effectiveMaxFrac
        }
        const num = parseNumberWithPrecision(value, effectiveMaxFrac)

        if (Number.isNaN(num) || !Number.isFinite(num)) {
          setDisplayValue('')
          break
        }

        if (Math.abs(num) > MAX_SAFE_DIGITS) {
          const valueStr = String(value)
          const maxValStr = typeof local.max === 'number' ? String(local.max) : String(DEFAULT_MAX_CURRENCY)
          if (compareNumericStrings(valueStr, maxValStr) > 0) {
            setDisplayValue('')
            break
          }
        }

        const { group: grp, decimal: dec } = numberSymbols()
        const nf = formatCurrency(num, baseCurrency, local.currencyDisplay || 'symbol', opts.minimumFractionDigits, effectiveMaxFrac, grp, dec)
        setDisplayValue(Number.isNaN(num) ? '' : nf)
        break
      }

      case 'percentage':
        setDisplayValue(formatPercentage(value))
        break

      case 'creditCard':
        setDisplayValue(formatCreditCard(value))
        break

      case 'custom': {
        if (typeof local.customMask === 'string') {
          const tokens = buildMaskTokens(local.customMask)
          setDisplayValue(applyMask(value, tokens, '_'))
          break
        }
        setDisplayValue(value)
        break
      }

      default:
        setDisplayValue(value)
    }
  }

  /** Encuentra la posición en `s` donde hay `left` caracteres que cumplen `test`. */
  const caretFromAccepted = (s: string, left: number, test: (c: string) => boolean) => {
    if (left <= 0) {
      for (let i = 0; i < s.length; i++) if (test(s[i])) return i
      return 0
    }
    let seen = 0
    for (let i = 0; i < s.length; i++) {
      if (test(s[i])) {
        seen++
        if (seen === left) return i + 1
      }
    }
    return s.length
  }

  const handleKeyDown = (e: KeyboardEvent & { currentTarget: HTMLInputElement }) => {
    const key = e.key
    const el = e.currentTarget
    const currentValue = el.value
    const pos = el.selectionStart ?? currentValue.length

    // Para máscaras con literales (% en percentage, - () en custom, etc.):
    // si el usuario presiona Backspace y el carácter antes del cursor es un
    // literal, borramos el dígito/char anterior al literal en vez del literal.
    if (key === 'Backspace' && pos > 0) {
      const charBefore = currentValue[pos - 1]
      const isLiteral =
        local.maskType === 'percentage'
          ? charBefore === '%'
          : local.maskType === 'currency'
            ? !/\d/.test(charBefore) && charBefore !== '.'
            : local.maskType === 'custom'
              ? !/[A-Za-z0-9]/.test(charBefore)
              : false
      if (isLiteral) {
        e.preventDefault()
        // Buscar el carácter significativo anterior al literal y borrarlo
        for (let i = pos - 2; i >= 0; i--) {
          const isSignificant = local.maskType === 'custom' ? /[A-Za-z0-9]/.test(currentValue[i]) : /\d/.test(currentValue[i])
          if (isSignificant) {
            const cleaned = currentValue.slice(0, i) + currentValue.slice(i + 1)
            // Re-serializar como si el usuario hubiera escrito este valor
            setRawValue(local.maskType === 'currency' ? cleaned.replace(/[^0-9.]/g, '') : cleaned.replace(/[^\d.]/g, ''))
            updateDisplayValue(local.maskType === 'currency' ? cleaned.replace(/[^0-9.]/g, '') : cleaned.replace(/[^\d.]/g, ''))
            if (local.onValueChange) {
              const raw = local.maskType === 'currency' ? cleaned.replace(/[^0-9.]/g, '') : cleaned.replace(/[^\d.]/g, '')
              local.onValueChange(raw)
            }
            break
          }
        }
        return
      }
    }

    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)) {
      return
    }

    if (e.ctrlKey || e.metaKey) {
      return
    }

    let isValid = false

    switch (local.maskType) {
      case 'currency': {
        const { decimal } = numberSymbols()
        const allowDecimal = (maxFractionDigitsAllowed() ?? 0) > 0

        if (/^\d$/.test(key)) {
          const newValue = currentValue + key

          if (newValue.length > MAX_STRING_LENGTH) {
            isValid = false
          } else {
            const numValue = parseNumberWithPrecision(newValue, maxFractionDigitsAllowed() ?? 2)

            if (Number.isNaN(numValue) || !Number.isFinite(numValue)) {
              isValid = false
            } else if (Math.abs(numValue) > MAX_SAFE_DIGITS) {
              const maxValStr = typeof local.max === 'number' ? String(local.max) : String(DEFAULT_MAX_CURRENCY)
              isValid = compareNumericStrings(newValue, maxValStr) <= 0
            } else {
              const maxVal = typeof local.max === 'number' ? local.max : DEFAULT_MAX_CURRENCY
              isValid = numValue <= maxVal
            }
          }
        } else if (allowDecimal && (key === '.' || key === decimal)) {
          isValid = !currentValue.includes('.')
        }

        if (isValid && currentValue.includes('.')) {
          const parts = currentValue.split('.')
          if (parts[1] && parts[1].length >= (maxFractionDigitsAllowed() ?? 0)) {
            isValid = false
          }
        }
        break
      }

      case 'percentage': {
        if (/^\d$/.test(key)) {
          const newValue = currentValue + key
          const numValue = parseNumberWithPrecision(newValue, 2)
          const maxVal = typeof local.max === 'number' ? local.max : 100
          isValid = numValue <= maxVal
        } else if (key === '.' && !currentValue.includes('.')) {
          isValid = true
        }
        if (isValid && currentValue.includes('.')) {
          const parts = currentValue.split('.')
          const maxFrac = 10
          if (parts[1] && parts[1].length >= maxFrac) {
            isValid = false
          }
        }
        break
      }

      case 'creditCard': {
        if (/^\d$/.test(key)) {
          isValid = currentValue.replace(/\D/g, '').length < 16
        }
        break
      }

      case 'custom': {
        if (typeof local.customMask === 'string') {
          const tokens = buildMaskTokens(local.customMask)
          const maxLen = maxInputLenForMask(tokens)

          const currentAlnumCount = currentValue.replace(/[^A-Za-z0-9]/g, '').length

          if (currentAlnumCount >= maxLen) {
            isValid = false
          } else {
            let tokenIndex = 0
            let targetToken: (typeof tokens)[0] | null = null

            for (const token of tokens) {
              if (token.kind !== 'literal') {
                if (tokenIndex === currentAlnumCount) {
                  targetToken = token
                  break
                }
                tokenIndex++
              }
            }

            if (targetToken) {
              isValid = isAllowedForToken(key, targetToken)
            } else {
              isValid = false
            }
          }
        } else {
          isValid = true
        }
        break
      }

      default:
        isValid = true
    }

    if (!isValid) {
      e.preventDefault()
    }
  }

  const handleInput = (e: InputEvent & { currentTarget: HTMLInputElement }) => {
    const el = e.currentTarget
    const inputValue = el.value
    let cleanedValue = inputValue

    switch (local.maskType) {
      case 'currency': {
        const { group, decimal } = numberSymbols()
        let s = inputValue.split(group).join('')
        if (decimal !== '.') s = s.split(decimal).join('.')
        s = s.replace(/[^0-9.]/g, '')
        cleanedValue = s

        const parts = cleanedValue.split('.')
        if (parts.length > 2) {
          cleanedValue = `${parts[0]}.${parts.slice(1).join('')}`
        }

        const maxFrac = 10
        if (parts[1] && parts[1].length > maxFrac) {
          cleanedValue = `${parts[0]}.${parts[1].substring(0, maxFrac)}`
        }

        if ((maxFractionDigitsAllowed() || 0) === 0) {
          cleanedValue = cleanedValue.replace(/\./g, '')
        }
        if (cleanedValue.length > MAX_STRING_LENGTH) {
          cleanedValue = cleanedValue.substring(0, MAX_STRING_LENGTH)
        }

        const numValue = parseNumberWithPrecision(cleanedValue, maxFractionDigitsAllowed() ?? 10)

        if (Number.isNaN(numValue) || !Number.isFinite(numValue)) {
          cleanedValue = '0'
        } else if (Math.abs(numValue) > MAX_SAFE_DIGITS) {
          const minValStr = typeof local.min === 'number' ? String(local.min) : '0'
          const maxValStr = typeof local.max === 'number' ? String(local.max) : String(DEFAULT_MAX_CURRENCY)

          if (compareNumericStrings(cleanedValue, minValStr) < 0) {
            cleanedValue = String(local.min || 0)
          } else if (compareNumericStrings(cleanedValue, maxValStr) > 0) {
            cleanedValue = String(local.max || DEFAULT_MAX_CURRENCY)
          }
        } else {
          if (typeof local.min === 'number' && numValue < local.min) {
            cleanedValue = String(local.min)
          }
          const maxVal = typeof local.max === 'number' ? local.max : DEFAULT_MAX_CURRENCY
          if (numValue > maxVal) {
            cleanedValue = String(maxVal)
          }
        }
        break
      }

      case 'custom': {
        if (typeof local.customMask === 'string') {
          const tokens = buildMaskTokens(local.customMask)
          const maxLen = maxInputLenForMask(tokens)

          const rawChars: string[] = []
          let inputIndex = 0

          for (const token of tokens) {
            if (token.kind === 'literal') continue

            let foundValidChar = false
            while (inputIndex < inputValue.length && !foundValidChar) {
              const ch = inputValue[inputIndex++]
              if (isAllowedForToken(ch, token)) {
                rawChars.push(ch)
                foundValidChar = true
              }
            }

            if (!foundValidChar) {
              break
            }

            if (rawChars.length >= maxLen) break
          }

          cleanedValue = rawChars.join('')
        }
        break
      }

      case 'percentage': {
        let s = inputValue.replace(/[^0-9.]/g, '')
        const parts = s.split('.')
        if (parts.length > 2) {
          s = `${parts[0]}.${parts.slice(1).join('')}`
        }
        const maxFrac = 10
        if (parts[1] && parts[1].length > maxFrac) {
          s = `${parts[0]}.${parts[1].substring(0, maxFrac)}`
        }
        cleanedValue = s
        const numValue = parseNumberWithPrecision(cleanedValue, 10)
        const maxVal = typeof local.max === 'number' ? local.max : 100
        if (typeof local.min === 'number' && numValue < local.min) {
          cleanedValue = String(local.min)
        }
        if (numValue > maxVal) {
          cleanedValue = String(maxVal)
        }
        break
      }

      case 'creditCard': {
        cleanedValue = inputValue.replace(/\D/g, '').substring(0, 16)
        break
      }
    }

    setRawValue(cleanedValue)
    updateDisplayValue(cleanedValue)

    if (local.onValueChange) {
      local.onValueChange(cleanedValue)
    }
  }

  const handleFocus = () => {
    setHasFocus(true)
  }

  const handleBlur = () => {
    setHasFocus(false)
  }

  // Sincronizar el valor visible del input y la posición del cursor en un
  // solo bloque síncrono. Si usáramos value={displayValue()} en el JSX,
  // SolidJS escribiría input.value en su ciclo de reconciliación, lo cual
  // SIEMPRE resetea la posición del cursor del navegador. Al manejarlo
  // manualmente en un effect, controlamos el orden: primero el value,
  // luego setSelectionRange, todo en la misma pila de ejecución.
  createEffect(() => {
    const dv = displayValue()
    if (!inputRef) return

    const wasFocused = document.activeElement === inputRef
    // Leer la posición del cursor ANTES de cambiar el value del input.
    // El navegador pierde la selección al modificar input.value, así que
    // la capturamos primero.
    const oldStart = wasFocused ? (inputRef.selectionStart ?? inputRef.value.length) : dv.length
    const oldVal = inputRef.value

    if (inputRef.value !== dv) {
      inputRef.value = dv
    }

    if (wasFocused) {
      // Calcular cuántos caracteres "significativos" había antes del cursor
      // en el valor viejo, y encontrar esa misma cantidad en el nuevo.
      const isCurrencyNoDecimals = local.maskType === 'currency' && (maxFractionDigitsAllowed() ?? 0) === 0

      const newPos = (() => {
        if (isCurrencyNoDecimals) {
          // Separadores de miles se reubican → siempre al final
          return dv.length
        }

        if (local.maskType === 'percentage') {
          // El sufijo "%" se agrega al final → siempre al final
          return dv.length
        }

        const before = oldVal.slice(0, oldStart)

        if (local.maskType === 'custom') {
          // Para custom (teléfonos, etc): contar caracteres alfanuméricos
          const left = (before.match(/[A-Za-z0-9]/g) || []).length
          return caretFromAccepted(dv, left, c => /[A-Za-z0-9]/.test(c))
        }

        // currency con decimales, percentage, creditCard: contar dígitos
        const left = (before.match(/\d/g) || []).length
        return caretFromAccepted(dv, left, c => /\d/.test(c))
      })()

      try {
        inputRef.setSelectionRange(newPos, newPos)
      } catch {
        /* empty */
      }
    }
  })

  const _placeholder = createMemo(() => {
    if (inputProps.placeholder) {
      return inputProps.placeholder
    }

    switch (local.maskType) {
      case 'currency': {
        const fd = fractionDigitsUsed()
        const base = 0
        const { group: grp, decimal: dec } = numberSymbols()
        return formatCurrency(base, local.currency || 'USD', local.currencyDisplay || 'symbol', 0, fd, grp, dec)
      }
      case 'percentage':
        return '0%'
      case 'creditCard':
        return '0000 0000 0000 0000'
      default:
        return ''
    }
  })

  const hasError = () => Boolean(resolvedError() && String(resolvedError()).length > 0)

  const baseClasses = createMemo(() =>
    twMerge(
      'focus:outline-none focus:ring-0',
      'focus:border-primary',
      'bg-transparent',
      local.variant === 'text' ? 'border-b-2' : '',
      local.variant === 'outlined' ? 'border-2' : '',
      // Un texto de ayuda no es un error: sólo `error` pinta el borde en rojo.
      ['text', 'outlined'].includes(local.variant ?? 'default') && hasError() ? 'border-danger' : 'border-border-strong',
      // Sin `border` el campo quedaba 2px más bajo y sin marco que el Input.
      local.variant === 'default' ? 'border bg-surface-muted' : '',
      local.variant === 'default' && hasError() ? 'border border-danger' : '',
      hasError() ? 'focus:border-danger' : ''
    )
  )

  const hasStartAdornment = () => !!local.startIcon
  const hasEndAdornment = () => !!local.endIcon

  const sizeConfig = createMemo(() => controlSize(local.size))

  return (
    <div class="relative flex flex-col">
      {/* Campo hidden con el valor real para el formulario */}
      {nameProps.name && <input type="hidden" name={nameProps.name} value={_rawValue()} />}
      <div class="relative overflow-hidden text-ellipsis truncate">
        {/* Start Icon */}
        <Show when={local.startIcon}>
          <div class="absolute inset-y-0 start-0 flex items-center ps-3.5 text-foreground-subtle z-10 pointer-events-none">
            <Icon icon={local.startIcon as string} width={sizeConfig().icon} class="opacity-90" />
          </div>
        </Show>
        <input
          {...inputProps}
          id={id}
          type="text"
          inputMode={
            local.maskType === 'currency' || local.maskType === 'percentage' || (local.maskType === 'custom' && typeof local.customMask === 'string' && isNumericMask(local.customMask))
              ? 'numeric'
              : undefined
          }
          class={twMerge(
            baseClasses(),
            sizeConfig().padX,
            hasStartAdornment() ? 'ps-12' : '',
            hasEndAdornment() ? 'pe-12' : '',
            // Altura fija de la escala común, igual que Input y Button.
            `block rounded-lg duration-100 ${inputProps.label ? sizeConfig().labelPad : 'py-0'} w-full text-foreground appearance-none peer`,
            sizeConfig().text,
            sizeConfig().height,
            inputProps.label && inputProps.placeholder ? '' : ''
          )}
          placeholder={inputProps.label && inputProps.placeholder ? (isLabelFloated() ? inputProps.placeholder : '') : inputProps.label ? ' ' : inputProps.placeholder || ''}
          ref={el => (inputRef = el)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-label={inputProps.label}
          aria-invalid={resolvedError() ? 'true' : undefined}
          aria-describedby={`${id}-message`}
        />

        {/* End Icon / Adornment */}
        <Show when={local.endIcon}>
          <div class={twMerge('absolute inset-y-0 end-0 flex items-center pe-1.5 z-20', sizeConfig().adornmentChild)}>
            {typeof local.endIcon === 'string' ? (
              <Icon icon={local.endIcon} width={sizeConfig().icon} class="opacity-90 text-foreground-subtle" />
            ) : isIconifyIcon(local.endIcon) ? (
              <Icon icon={local.endIcon} width={sizeConfig().icon} class="opacity-90 text-foreground-subtle" />
            ) : (
              local.endIcon
            )}
          </div>
        </Show>

        <Show when={inputProps.label}>
          <label
            for={id}
            class={twMerge(
              `absolute
              block
              items-center
              select-none
              whitespace-nowrap
              overflow-hidden
              text-ellipsis
              max-w-[calc(100%-1rem)]
              duration-100
              transition-all
              z-10 origin-left
              cursor-pointer
              ${hasStartAdornment() ? 'start-12' : sizeConfig().padStart}`,
              isLabelFloated() ? [`${sizeConfig().labelFloating} top-1 translate-y-0 text-foreground-muted`] : [`${sizeConfig().text} top-1/2 -translate-y-1/2 text-foreground`],
              resolvedError()
                ? `text-danger ${isLabelFloated() ? '' : 'peer-placeholder-shown:text-danger'}`
                : `text-foreground-muted ${isLabelFloated() ? '' : 'peer-placeholder-shown:text-foreground'}`
            )}
            onClick={() => inputRef?.focus()}
          >
            {inputProps.label}
          </label>
        </Show>
      </div>

      <FieldMessage id={`${id}-message`} size={local.size} error={resolvedError()} helperText={inputProps.helperText} class={hasStartAdornment() ? 'ps-12' : ''} />
    </div>
  )
}

export default MaskInput
